import React, { Fragment, useState, useEffect, useContext } from "react";
import Sidebar from "../components/Sidebar";
import { auth } from "../firebaseConfig";
import { fetchClothes, deleteClothing, toggleFavori, deleteAllClothesForUser } from "../components/clothingService";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Pagination, Navigation, FreeMode } from "swiper/modules";
import AddClothingModal from "../components/AddClothingModal";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, Transition } from "@headlessui/react";
import { AuthContext } from "../context/AuthContext";
import { useImport } from "../context/ImportContext";
import { PLAN_LIMITS } from "../services/userService";
import { ALWAYS_PREMIUM } from "../config/featureFlags";
import { 
    Plus, 
    Search, 
    Filter, 
    Grid3X3, 
    List, 
    Heart, 
    Edit3, 
    Trash2, 
    Star,
    MoreVertical,
    Shirt
} from "lucide-react";
import ToastContainer from "../components/ToastContainer";
import useToast from "../hooks/useToast";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/free-mode";
import "../styles/Dashboard.css";

const Dressing = () => {
    const { userProfile } = useContext(AuthContext);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedClothing, setSelectedClothing] = useState(null);
    const [clothes, setClothes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedFilter, setSelectedFilter] = useState("all");
    const [viewMode, setViewMode] = useState("swiper"); // "swiper" ou "grid"
    const [showFilters, setShowFilters] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [confirmClearAll, setConfirmClearAll] = useState(false);
    const [isClearingAll, setIsClearingAll] = useState(false);
    const { toasts, removeToast, success, error } = useToast();
    const isTouch = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const { lastAdded } = useImport();

    const handleOpenModal = (clothing = null) => {
        // Si on édite un vêtement existant, pas de vérification de limite
        if (clothing) {
            setSelectedClothing(clothing);
            setIsModalOpen(true);
            return;
        }
        
        // Vérifier la limite AVANT d'ouvrir le modal pour éviter de gaspiller des tokens IA
        const plan = userProfile?.plan || "free";
        // Ne pas confondre null (illimité) avec undefined (inconnu)
        let maxClothes;
        if (ALWAYS_PREMIUM) {
            maxClothes = null;
        } else {
            const userLimit = userProfile?.limits?.maxClothes;
            const planLimit = PLAN_LIMITS[plan]?.maxClothes;
            if (userLimit !== undefined) {
                maxClothes = userLimit; // null => illimité
            } else if (planLimit !== undefined) {
                maxClothes = planLimit; // null => illimité
            } else {
                maxClothes = PLAN_LIMITS.free.maxClothes;
            }
        }

        if (maxClothes !== null && clothes.length >= maxClothes) {
            error(`Limite atteinte ! Vous avez déjà ${maxClothes} vêtements. Passez au Premium pour un ajout illimité.`);
            return;
        }
        
        setSelectedClothing(null);
        setIsModalOpen(true);
    };

    useEffect(() => {
        const fetchClothesData = async () => {
            if (!auth.currentUser) {
                setClothes([]);
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const clothesList = await fetchClothes(auth.currentUser.uid);
                setClothes(clothesList);
            } catch (err) {
                console.error("Erreur lors du chargement des vêtements :", err);
                error("Erreur lors du chargement des vêtements");
            } finally {
                setIsLoading(false);
            }
        };

        fetchClothesData();
    }, [isModalOpen, error]);

    // Ajouts en temps réel via import arrière-plan
    useEffect(() => {
        if (!lastAdded || !auth.currentUser) return;
        if (lastAdded.userId !== auth.currentUser.uid) return;
        setClothes(prev => {
            if (prev.find(i => i.id === lastAdded.id)) return prev;
            return [lastAdded, ...prev];
        });
    }, [lastAdded]);

    const confirmDelete = async () => {
        if (!pendingDelete) return;

        try {
            setIsDeleting(true);
            await deleteClothing(pendingDelete.id);
            setClothes(prevClothes => prevClothes.filter(item => item.id !== pendingDelete.id));
            success("Vêtement supprimé avec succès");
        } catch (err) {
            console.error("Erreur lors de la suppression du vêtement :", err);
            error("Erreur lors de la suppression");
        } finally {
            setIsDeleting(false);
            setPendingDelete(null);
        }
    };

    const cancelDelete = () => {
        if (isDeleting) return;
        setPendingDelete(null);
    };

    const handleClearAll = async () => {
        if (!auth.currentUser) return;
        try {
            setIsClearingAll(true);
            const removed = await deleteAllClothesForUser(auth.currentUser.uid);
            setClothes([]);
            success(removed > 0 ? `Dressing vidé (${removed} supprimé${removed > 1 ? 's' : ''})` : "Aucun vêtement à supprimer");
        } catch (err) {
            console.error("Erreur lors du vidage du dressing :", err);
            error("Erreur lors de la suppression complète");
        } finally {
            setIsClearingAll(false);
            setConfirmClearAll(false);
        }
    };

    const handleToggleFavori = async (id, currentFavoriStatus) => {
        try {
            await toggleFavori(id, currentFavoriStatus);
            setClothes(prevClothes =>
                prevClothes.map(item =>
                    item.id === id ? { ...item, favori: !currentFavoriStatus } : item
                )
            );
            success(currentFavoriStatus ? "Retiré des favoris" : "Ajouté aux favoris");
        } catch (err) {
            console.error("Erreur :", err);
            error("Erreur lors de la modification");
        }
    };

    // Fonctions de filtrage et recherche
    const filteredClothes = clothes.filter(item => {
        const search = searchTerm.toLowerCase();
        // Support both old color string and new colors array
        const colorMatch = Array.isArray(item.colors) 
            ? item.colors.some(c => c?.toLowerCase().includes(search))
            : item.color?.toLowerCase().includes(search);
        const matchesSearch =
            item.type?.toLowerCase().includes(search) ||
            item.title?.toLowerCase().includes(search) ||
            colorMatch;
        
        if (selectedFilter === "favorites") {
            return matchesSearch && item.favori;
        }
        if (selectedFilter === "recent") {
            const oneWeekAgo = new Date();
            oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
            return matchesSearch && item.createdAt?.toDate() > oneWeekAgo;
        }
        return matchesSearch;
    });

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center space-y-6"
                >
                    <div className="relative">
                        <div className="w-16 h-16 border-4 border-gray-100 rounded-full"></div>
                        <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-black rounded-full animate-spin"></div>
                    </div>
                    <div className="text-center">
                        <motion.p 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="text-gray-600 font-medium text-lg"
                        >
                            Chargement de votre dressing...
                        </motion.p>
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            <Sidebar />
            
            <main className="ml-0 md:ml-24 px-4 py-6 md:px-8 pb-20 md:pb-6">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="max-w-7xl mx-auto"
                >
                    {/* Header */}
                    <motion.div variants={itemVariants} className="mb-8">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                            <div>
                                <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2">
                                    Mon Dressing
                                </h1>
                                <p className="text-gray-600">
                                    {filteredClothes.length} vêtement{filteredClothes.length > 1 ? 's' : ''}
                                </p>
                            </div>
                            
                            <div className="mt-4 md:mt-0 flex items-center gap-3">
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <button
                                        onClick={() => handleOpenModal()}
                                        className="inline-flex items-center px-6 py-3 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition-colors shadow-soft"
                                    >
                                        <Plus className="w-5 h-5 mr-2" />
                                        Ajouter un vêtement
                                    </button>
                                </motion.div>
                                {clothes.length > 0 && (
                                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                        <button
                                            onClick={() => setConfirmClearAll(true)}
                                            className="inline-flex items-center px-4 py-3 bg-white text-red-600 border border-red-200 rounded-full font-medium hover:bg-red-50 transition-colors"
                                        >
                                            <Trash2 className="w-5 h-5 mr-2" />
                                            Vider le dressing
                                        </button>
                                    </motion.div>
                                )}
                            </div>
                        </div>

                        {/* Barre de recherche et filtres */}
                        <div className="flex flex-col md:flex-row gap-4 mb-6">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Rechercher un vêtement..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                                />
                            </div>
                            
                            <div className="flex gap-2">
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => setShowFilters(!showFilters)}
                                    className={`px-4 py-3 rounded-xl border transition-all ${
                                        showFilters 
                                            ? 'bg-black text-white border-black' 
                                            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <Filter className="w-5 h-5" />
                                </motion.button>
                                
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => setViewMode(viewMode === "swiper" ? "grid" : "swiper")}
                                    className="px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-300 transition-all"
                                >
                                    {viewMode === "swiper" ? <Grid3X3 className="w-5 h-5" /> : <List className="w-5 h-5" />}
                                </motion.button>
                            </div>
                        </div>

                        {/* Filtres */}
                        <AnimatePresence>
                            {showFilters && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200"
                                >
                                    <div className="flex flex-wrap gap-2">
                                        {[
                                            { key: "all", label: "Tous" },
                                            { key: "favorites", label: "Favoris" },
                                            { key: "recent", label: "Récents" }
                                        ].map((filter) => (
                                            <motion.button
                                                key={filter.key}
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onClick={() => setSelectedFilter(filter.key)}
                                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                                    selectedFilter === filter.key
                                                        ? 'bg-black text-white'
                                                        : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
                                                }`}
                                            >
                                                {filter.label}
                                            </motion.button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>

                    {/* Contenu principal */}
                    <motion.div variants={itemVariants}>
                        <AddClothingModal
                            isOpen={isModalOpen}
                            onClose={() => setIsModalOpen(false)}
                            initialData={selectedClothing}
                            isEditing={!!selectedClothing}
                        />

                        {filteredClothes.length > 0 ? (
                            viewMode === "swiper" ? (
                                <div className="relative">
                                    <Swiper
                                        effect={isTouch ? undefined : "coverflow"}
                                        grabCursor={true}
                                        centeredSlides={true}
                                        loop={!isTouch && filteredClothes.length >= 10}
                                        navigation={true}
                                        pagination={{ clickable: true }}
                                        coverflowEffect={isTouch ? undefined : {
                                            rotate: 0,
                                            stretch: 0,
                                            depth: 160,
                                            modifier: 1.8,
                                            slideShadows: false,
                                        }}
                                        breakpoints={{
                                            320: {
                                                slidesPerView: 1.2,
                                                spaceBetween: 12,
                                                loop: !isTouch && filteredClothes.length >= 3
                                            },
                                            640: {
                                                slidesPerView: 2.2,
                                                spaceBetween: 14,
                                                loop: !isTouch && filteredClothes.length >= 5
                                            },
                                            1024: {
                                                slidesPerView: 3.2,
                                                spaceBetween: 16,
                                                loop: !isTouch && filteredClothes.length >= 7
                                            },
                                        }}
                                        speed={isTouch ? 320 : 400}
                                        longSwipes={true}
                                        longSwipesRatio={0.1}
                                        longSwipesMs={100}
                                        roundLengths={true}
                                        preloadImages={false}
                                        watchSlidesProgress={true}
                                        observer={true}
                                        observeParents={true}
                                        modules={[EffectCoverflow, Pagination, Navigation, FreeMode]}
                                        className="dressing-swiper h-[600px] md:h-[700px]"
                                    >
                                        {filteredClothes.map((item, index) => (
                                            <SwiperSlide key={item.id}>
                                                <motion.div
                                                    initial={{ opacity: 0, scale: 0.8 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    transition={{ delay: index * 0.1 }}
                                                    className="relative bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 overflow-hidden"
                                                >
                                                    <div className="relative aspect-[3/4] overflow-hidden">
                                                        {item.image ? (
                                                            <motion.img
                                                                src={item.image}
                                                                alt={item.type}
                                                                className="w-full h-full object-cover"
                                                                whileHover={{ scale: 1.05 }}
                                                                transition={{ duration: 0.3 }}
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                                                <Shirt className="w-16 h-16 text-gray-400" />
                                                            </div>
                                                        )}

                                                        {item.favori && (
                                                            <motion.div 
                                                                className="absolute top-3 left-3"
                                                                initial={{ scale: 0 }}
                                                                animate={{ scale: 1 }}
                                                                transition={{ delay: index * 0.1 + 0.3, type: "spring" }}
                                                            >
                                                                <div className="bg-yellow-400 p-2 rounded-full shadow-sm">
                                                                    <Star className="w-4 h-4 text-white fill-current" />
                                                                </div>
                                                            </motion.div>
                                                        )}

                                                        <motion.div 
                                                            className="absolute top-3 right-3"
                                                            initial={{ opacity: 0 }}
                                                            animate={{ opacity: 1 }}
                                                            transition={{ delay: index * 0.1 + 0.5 }}
                                                        >
                                                            <div className="relative group">
                                                                <button className="p-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-all">
                                                                    <MoreVertical className="w-4 h-4 text-gray-600" />
                                                                </button>
                                                                
                                                                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-medium opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                                                    <div className="p-2">
                                                                        <button
                                                                            className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                                                            onClick={() => handleOpenModal(item)}
                                                                        >
                                                                            <Edit3 className="w-4 h-4 mr-3" />
                                                                            Modifier
                                                                        </button>
                                                                        <button
                                                                            className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                                                                            onClick={() => handleToggleFavori(item.id, item.favori || false)}
                                                                        >
                                                                            <Heart className={`w-4 h-4 mr-3 ${item.favori ? 'text-red-500 fill-current' : 'text-gray-400'}`} />
                                                                            {item.favori ? "Retirer des favoris" : "Ajouter aux favoris"}
                                                                        </button>
                                                                        <button
                                                                            className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                            onClick={() => setPendingDelete(item)}
                                                                        >
                                                                            <Trash2 className="w-4 h-4 mr-3" />
                                                                            Supprimer
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </motion.div>
                                                    </div>
                                                    
                                                    <div className="p-4">
                                                        <h3 className="font-semibold text-gray-900 text-lg mb-1">
                                                            {item.title?.trim() || item.type}
                                                        </h3>
                                                        <p className="text-gray-500 text-sm capitalize">
                                                            {Array.isArray(item.colors) && item.colors.length > 0
                                                                ? item.colors.join(', ')
                                                                : item.color || "Couleur non spécifiée"}
                                                        </p>
                                                    </div>
                                                </motion.div>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                    {filteredClothes.map((item, index) => (
                                        <motion.div
                                            key={item.id}
                                            initial={{ opacity: 0, scale: 0.8 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ delay: index * 0.05 }}
                                            whileHover={{ y: -4 }}
                                            className="bg-white rounded-xl overflow-hidden shadow-soft hover:shadow-medium border border-gray-100 transition-all duration-300 cursor-pointer"
                                        >
                                            <div className="relative aspect-square overflow-hidden">
                                                {item.image ? (
                                                    <motion.img
                                                        src={item.image}
                                                        alt={item.type}
                                                        className="w-full h-full object-cover"
                                                        whileHover={{ scale: 1.1 }}
                                                        transition={{ duration: 0.3 }}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                                        <Shirt className="w-8 h-8 text-gray-400" />
                                                    </div>
                                                )}
                                                
                                                {item.favori && (
                                                    <div className="absolute top-2 right-2">
                                                        <div className="bg-yellow-400 p-1.5 rounded-full shadow-sm">
                                                            <Star className="w-3 h-3 text-white fill-current" />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div className="p-3">
                                                <h3 className="font-medium text-gray-900 text-sm truncate capitalize">
                                                    {item.title?.trim() || item.type}
                                                </h3>
                                                <p className="text-gray-500 text-xs capitalize">
                                                    {Array.isArray(item.colors) && item.colors.length > 0
                                                        ? item.colors.join(', ')
                                                        : item.color || "Non spécifiée"}
                                                </p>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )
                        ) : (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="text-center py-16"
                            >
                                <motion.div
                                    animate={{ 
                                        scale: [1, 1.1, 1],
                                        rotate: [0, 5, -5, 0]
                                    }}
                                    transition={{ 
                                        duration: 2, 
                                        repeat: Infinity,
                                        repeatDelay: 3
                                    }}
                                >
                                    <Shirt className="w-16 h-16 text-gray-300 mx-auto mb-6" />
                                </motion.div>
                                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                                    {searchTerm || selectedFilter !== "all" ? "Aucun résultat" : "Aucun vêtement"}
                                </h3>
                                <p className="text-gray-500 mb-6">
                                    {searchTerm || selectedFilter !== "all" 
                                        ? "Essayez de modifier vos critères de recherche" 
                                        : "Commencez par ajouter votre premier vêtement"
                                    }
                                </p>
                                <motion.div
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <button
                                        onClick={() => handleOpenModal()}
                                        className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors shadow-soft"
                                    >
                                        <Plus className="w-5 h-5 mr-2" />
                                        Ajouter un vêtement
                                    </button>
                                </motion.div>
                            </motion.div>
                        )}
                    </motion.div>
                </motion.div>
            </main>
            
            <ToastContainer toasts={toasts} onRemove={removeToast} />

            <Transition show={!!pendingDelete} as={Fragment}>
                <Dialog onClose={cancelDelete} className="relative z-[60]">
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                    </Transition.Child>

                    <div className="fixed inset-0 px-4 flex items-center justify-center">
                        <Transition.Child
                            as={Fragment}
                            enter="transition-all ease-out duration-200"
                            enterFrom="opacity-0 translate-y-2 scale-95"
                            enterTo="opacity-100 translate-y-0 scale-100"
                            leave="transition-all ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0 scale-100"
                            leaveTo="opacity-0 translate-y-1 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md rounded-3xl bg-white shadow-[0_24px_70px_rgba(15,23,42,0.2)] border border-white/60">
                                <div className="px-6 pt-6 pb-5 text-center space-y-4">
                                    <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                                        <Trash2 className="w-6 h-6" />
                                    </div>
                                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                                        Supprimer ce vêtement ?
                                    </Dialog.Title>
                                    <Dialog.Description className="text-sm text-gray-600">
                                        {pendingDelete?.type ? `“${pendingDelete.type}” sera retiré définitivement de votre dressing.` : "Cette pièce sera retirée définitivement de votre dressing."}
                                    </Dialog.Description>
                                </div>
                                <div className="px-6 pb-6 flex flex-col sm:flex-row sm:justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={cancelDelete}
                                        disabled={isDeleting}
                                        className="h-11 px-5 rounded-xl border border-gray-200 bg-white/80 hover:bg-white disabled:opacity-60"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="button"
                                        onClick={confirmDelete}
                                        disabled={isDeleting}
                                        className="h-11 px-5 rounded-xl bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                                    >
                                        {isDeleting ? "Suppression…" : "Supprimer"}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition>

            <Transition show={confirmClearAll} as={Fragment}>
                <Dialog onClose={() => { if (!isClearingAll) setConfirmClearAll(false); }} className="relative z-[60]">
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                    </Transition.Child>

                    <div className="fixed inset-0 px-4 flex items-center justify-center">
                        <Transition.Child
                            as={Fragment}
                            enter="transition-all ease-out duration-200"
                            enterFrom="opacity-0 translate-y-2 scale-95"
                            enterTo="opacity-100 translate-y-0 scale-100"
                            leave="transition-all ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0 scale-100"
                            leaveTo="opacity-0 translate-y-1 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md rounded-3xl bg-white shadow-[0_24px_70px_rgba(15,23,42,0.2)] border border-white/60">
                                <div className="px-6 pt-6 pb-5 text-center space-y-4">
                                    <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                                        <Trash2 className="w-6 h-6" />
                                    </div>
                                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                                        Vider tout le dressing ?
                                    </Dialog.Title>
                                    <Dialog.Description className="text-sm text-gray-600">
                                        {clothes.length > 0
                                            ? `Vous allez supprimer définitivement ${clothes.length} vêtement${clothes.length > 1 ? 's' : ''}. Cette action est irréversible.`
                                            : "Votre dressing est déjà vide."}
                                    </Dialog.Description>
                                </div>
                                <div className="px-6 pb-6 flex flex-col sm:flex-row sm:justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => !isClearingAll && setConfirmClearAll(false)}
                                        disabled={isClearingAll}
                                        className="h-11 px-5 rounded-xl border border-gray-200 bg-white/80 hover:bg-white disabled:opacity-60"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleClearAll}
                                        disabled={isClearingAll || clothes.length === 0}
                                        className="h-11 px-5 rounded-xl bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                                    >
                                        {isClearingAll ? "Suppression…" : "Tout supprimer"}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
};

export default Dressing;
